import React, { createContext, useContext, useMemo, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { IActivity, IMotif } from '@leedpulse-monorepo/utils';
import AuthenticationContext from '../../auth/AuthenticationContext';
import Domain from '../../../../domain/models/Domain';
import updateDomain from '../../../../domain/use_cases/domains/updateDomain';
import deleteDomain from '../../../../domain/use_cases/domains/deleteDomain';

export interface DomainEditionFormContextProps {
  wipDomain: Domain | null;
  isDomainChanged: boolean;
  updateStatus: ContextStatus;
  deleteStatus: ContextStatus;
  initWithDomain: (domain: Domain) => void;
  submitUpdate: () => Promise<void>;
  submitDelete: () => Promise<void>;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onTagsChange: (tags: string[]) => void;
  onPositionsChange: (positions: string[]) => void;
  onNetworksChange: (networks: string[]) => void;
  onAiReceptionistPromptChange: (prompt: string) => void;
  onAiReceptionistFirstMessageChange: (first_message: string) => void;
  onActivitiesChange: (activities: IActivity[]) => void;
  onMotifsChange: (motifs: IMotif[]) => void;
  onArchivedChange: (archived: boolean) => void;
}

const DomainEditionFormContext = createContext<DomainEditionFormContextProps>({
  wipDomain: null,
  isDomainChanged: false,
  updateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
  initWithDomain: (_: Domain) => {return;},
  submitUpdate: async () => {return;},
  submitDelete: async () => {return;},
  onNameChange: (_: string) => {return;},
  onDescriptionChange: (_: string) => {return;},
  onTagsChange: (_: string[]) => {return;},
  onPositionsChange: (_: string[]) => {return;},
  onNetworksChange: (_: string[]) => {return;},
  onAiReceptionistPromptChange: (_: string) => {return;},
  onAiReceptionistFirstMessageChange: (_: string) => {return;},
  onActivitiesChange: (_: IActivity[]) => {return;},
  onMotifsChange: (_: IMotif[]) => {return;},
  onArchivedChange: (_: boolean) => {return;},
});

export const DomainEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [initialDomain, setInitialDomain] = useState<Domain | null>(null);
  const [wipDomain, setWipDomain] = useState<Domain | null>(null);
  const [updateStatus, setUpdateStatus] = useState(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState(ContextStatus.IDLE);

  const authContext = useContext(AuthenticationContext);

  const isDomainChanged = useMemo(() => {
    return wipDomain?.name !== initialDomain?.name ||
      wipDomain?.description !== initialDomain?.description ||
      wipDomain?.tags !== initialDomain?.tags ||
      wipDomain?.positions !== initialDomain?.positions ||
      wipDomain?.networks !== initialDomain?.networks ||
      wipDomain?.ai_receptionist?.prompt !== initialDomain?.ai_receptionist?.prompt ||
      wipDomain?.ai_receptionist?.first_message !== initialDomain?.ai_receptionist?.first_message ||
      wipDomain?.ai_receptionist?.activities !== initialDomain?.ai_receptionist?.activities ||
      wipDomain?.ai_receptionist?.motifs !== initialDomain?.ai_receptionist?.motifs ||
      wipDomain?.archived !== initialDomain?.archived;
  }, [wipDomain, initialDomain]);

  const initWithDomain = (domain: Domain) => {
    setWipDomain(domain);
    setInitialDomain(domain);
  };

  const submitUpdate = async () => {
    if (wipDomain === null || authContext.authentication === null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      await updateDomain(wipDomain, authContext.authentication);
      setUpdateStatus(ContextStatus.SUCCESS);
      setInitialDomain(wipDomain);
    } catch (error) {
      console.error('Error updating domain:', error);
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async () => {
    if (wipDomain === null || authContext.authentication === null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteDomain(wipDomain, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('Error deleting domain:', error);
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const onNameChange = (name: string) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        name,
      })
    );
  };

  const onDescriptionChange = (description: string) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        description,
      })
    );
  };

  const onTagsChange = (tags: string[]) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        tags,
      })
    );
  };

  const onPositionsChange = (positions: string[]) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        positions,
      })
    );
  };

  const onNetworksChange = (networks: string[]) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        networks,
      })
    );
  };

  const onAiReceptionistPromptChange = (prompt: string) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        ai_receptionist: { ...wipDomain.ai_receptionist, prompt },
      })
    );
  };

  const onAiReceptionistFirstMessageChange = (first_message: string) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        ai_receptionist: { ...wipDomain.ai_receptionist, first_message },
      })
    );
  };

  const onActivitiesChange = (activities: IActivity[]) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        ai_receptionist: { ...wipDomain.ai_receptionist, activities },
      })
    );
  };

  const onMotifsChange = (motifs: IMotif[]) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        ai_receptionist: { ...wipDomain.ai_receptionist, motifs },
      })
    );
  };

  const onArchivedChange = (archived: boolean) => {
    if (wipDomain === null) return;
    setWipDomain(
      wipDomain.copyWith({
        archived,
      })
    );
  };

  return (
    <DomainEditionFormContext.Provider
      value={{
        wipDomain,
        isDomainChanged,
        updateStatus,
        deleteStatus,
        initWithDomain,
        submitUpdate,
        submitDelete,
        onNameChange,
        onDescriptionChange,
        onTagsChange,
        onPositionsChange,
        onNetworksChange,
        onAiReceptionistPromptChange,
        onAiReceptionistFirstMessageChange,
        onActivitiesChange,
        onMotifsChange,
        onArchivedChange,
      }}
    >
      {children}
    </DomainEditionFormContext.Provider>
  );
};

export default DomainEditionFormContext;
